import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,RadarChart,PolarAngleAxis,PolarRadiusAxis,Radar,PolarGrid
} from 'recharts';
import {
  Users, Calendar, Target, TrendingUp, BarChart3, Clock, Filter,
  Loader2, AlertCircle, Check, X, Clock4, MapPin, Building,
  ChevronDown, ChevronUp, User, Activity, BookOpen, Network,Star, Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Enhanced color schemes
const CHART_COLORS = {
  primary: {
    purple: ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'],
    green: ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
    orange: ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA'],
    blue: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'],
    yellow: ['#CA8A04', '#EAB308', '#FACC15', '#FDE047', '#FEF08A']
  }
};

// Card color schemes
const CARD_SCHEMES = {
  purple: {
    background: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    accent: 'text-purple-600',
    border: 'border-purple-200',
  },
  green: {
    background: 'bg-green-50',
    icon: 'bg-green-100 text-green-600',
    accent: 'text-green-600',
    border: 'border-green-200',
  },
  orange: {
    background: 'bg-orange-50',
    icon: 'bg-orange-100 text-orange-600',
    accent: 'text-orange-600',
    border: 'border-orange-200',
  },
  blue: {
    background: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    accent: 'text-blue-600',
    border: 'border-blue-200',
  }
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'attendance', label: 'Attendance', icon: Users },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'venues', label: 'Venues', icon: MapPin }
];

const MetricCard = ({ title, value, icon: Icon, trend, subtext, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className={`${colors.background} rounded-2xl p-6 transition-all duration-300 
      hover:shadow-lg hover:scale-[1.02] relative overflow-hidden group border ${colors.border}`}
    >
      <div className="relative flex justify-between items-start">
        <div className={`p-3 rounded-xl ${colors.icon} transition-all duration-300 
          group-hover:scale-110`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center ${trend >= 0 ? 'text-green-600' : 'text-red-600'} 
            bg-white/80 px-2 py-1 rounded-lg`}>
            <span className="text-sm font-medium">
              {trend > 0 ? '+' : ''}{trend}%
            </span>
            {trend >= 0 ? (
              <ChevronUp className="h-4 w-4 ml-1" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-1" />
            )}
          </div>
        )}
      </div>
      <div className="relative mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-gray-600 text-sm mt-1 font-medium">{title}</p>
        {subtext && (
          <p className={`text-sm ${colors.accent} mt-2`}>{subtext}</p>
        )}
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, icon: Icon, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${colors.background}`}>
            <Icon className={`h-5 w-5 ${colors.accent}`} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const EventAnalytics = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('365');
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [timeRange]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events?range=${timeRange}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      setEvents(data.filter(event => event.status !== 'rejected')); // Filter out rejected events
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600" />
          <p className="text-purple-600 font-medium">Loading event analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchEvents}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
              transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Compute metrics
  const publishedEvents = events.filter(e => e.status === 'published');
  const virtualEvents = events.filter(e => e.venue.isVirtual);
  const physicalEvents = events.filter(e => !e.venue.isVirtual);
  const totalCapacity = events.reduce((sum, event) => sum + event.capacity.max, 0);
  const avgCapacity = Math.round(totalCapacity / (events.length || 1));
 // Function to prepare data for export
 const prepareExportData = () => {
  const virtualEvents = events.filter(e => e.venue.isVirtual);
  const physicalEvents = events.filter(e => !e.venue.isVirtual);
  const totalCapacity = events.reduce((sum, event) => sum + event.capacity.max, 0);
  const avgCapacity = Math.round(totalCapacity / (events.length || 1));

  return {
    overview: [
      ['Metric', 'Value'],
      ['Total Events', events.length],
      ['Average Capacity', avgCapacity],
      ['Virtual Events', virtualEvents.length],
      ['Physical Events', physicalEvents.length],
      ['Total Capacity', totalCapacity],
    ],
    
    eventTypes: [
      ['Type', 'Count', 'Percentage'],
      ...Object.entries(events.reduce((acc, event) => {
        acc[event.type] = (acc[event.type] || 0) + 1;
        return acc;
      }, {})).map(([type, count]) => [
        type,
        count,
        `${((count / events.length) * 100).toFixed(1)}%`
      ])
    ],
    
    venues: [
      ['Name', 'City', 'State', 'Type', 'Capacity'],
      ...events.map(event => [
        event.venue.name || 'Unnamed Venue',
        event.venue.city || 'N/A',
        event.venue.state || 'N/A',
        event.venue.isVirtual ? 'Virtual' : 'Physical',
        event.capacity.max
      ])
    ],
    
    timeline: [
      ['Event', 'Start Date', 'End Date', 'Status', 'Capacity'],
      ...events.map(event => {
        const startDate = new Date(event.schedule.startDate);
        const endDate = new Date(event.schedule.endDate);
        const now = new Date();
        let status = 'Upcoming';
        if (startDate <= now && endDate >= now) status = 'Ongoing';
        if (endDate < now) status = 'Completed';
        
        return [
          event.title,
          startDate.toLocaleDateString(),
          endDate.toLocaleDateString(),
          status,
          event.capacity.max
        ];
      })
    ]
  };
};

// Function to export to Excel
const exportToExcel = () => {
  const data = prepareExportData();
  const wb = XLSX.utils.book_new();
  
  // Add each section as a worksheet
  Object.entries(data).forEach(([sheetName, sheetData]) => {
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.charAt(0).toUpperCase() + sheetName.slice(1));
  });

  // Save the workbook
  XLSX.writeFile(wb, `event_analytics_${new Date().toLocaleDateString()}.xlsx`);
};

// Function to export to PDF
const exportToPDF = () => {
  const data = prepareExportData();
  const doc = new jsPDF();
  
  // Set initial position
  let yPos = 20;
  
  // Add title
  doc.setFontSize(16);
  doc.text('Event Analytics Report', 20, yPos);
  yPos += 10;
  
  // Add date and filters
  doc.setFontSize(10);
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPos);
  doc.text(`Time Range: ${timeRange} days`, 20, yPos + 5);
  yPos += 20;
  
  // Add sections
  Object.entries(data).forEach(([section, rows]) => {
    // Add section header
    doc.setFontSize(14);
    doc.setTextColor(82, 82, 255);
    doc.text(section.charAt(0).toUpperCase() + section.slice(1), 20, yPos);
    yPos += 10;
    
    // Reset text color
    doc.setTextColor(0);
    doc.setFontSize(10);
    
    // Add table data
    rows.forEach((row) => {
      // Check if we need a new page
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(row.join(' | '), 30, yPos);
      yPos += 7;
    });
    
    yPos += 10;
  });
  
  // Save the PDF
  doc.save(`event_analytics_${new Date().toLocaleDateString()}.pdf`);
};

// Replace the existing filter button with export dropdown
const exportButton = (
  <div className="relative">
    <button
      onClick={() => setShowExportMenu(!showExportMenu)}
      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 
        hover:bg-white/20 transition-colors border border-purple-400/30 
        text-white group"
    >
      <Download className="h-5 w-5 group-hover:scale-110 transition-transform" />
      <span>Export</span>
      <ChevronDown className="h-4 w-4" />
    </button>

    {showExportMenu && (
      <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg 
        ring-1 ring-black ring-opacity-5 z-50"
        onBlur={() => setShowExportMenu(false)}
      >
        <div className="py-1">
          <button
            onClick={() => {
              exportToExcel();
              setShowExportMenu(false);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
              hover:bg-gray-100 w-full text-left"
          >
            <FileSpreadsheet className="h-4 w-4 text-green-600" />
            Export as Excel
          </button>
          <button
            onClick={() => {
              exportToPDF();
              setShowExportMenu(false);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
              hover:bg-gray-100 w-full text-left"
          >
            <FileText className="h-4 w-4 text-blue-600" />
            Export as PDF
          </button>
        </div>
      </div>
    )}
  </div>
);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 pb-32 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">Event Analytics</h1>
              <p className="mt-2 text-purple-100">
                Comprehensive insights into event performance and metrics
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last quarter</option>
                  <option value="180">Last 6 months</option>
                  <option value="365">Last year</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              </div>

              <div className="flex gap-2">
      {exportButton}
      <button className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
        border border-purple-400/30 text-white group">
        <Filter className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>
      <button 
        onClick={fetchEvents}
        className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
          border border-purple-400/30 text-white group"
      >
        <Activity className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>
    </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-12 border-b border-purple-400/30">
            <div className="flex gap-8 overflow-x-auto pb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 pb-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-300
                    ${activeTab === tab.id
                      ? 'text-white border-white'
                      : 'text-purple-100 border-transparent hover:text-white hover:border-white/50'
                    }
                  `}
                >
                  <tab.icon className={`h-4 w-4 ${
                    activeTab === tab.id ? 'text-white' : 'text-purple-100'
                  }`} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="-mt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <MetricCard
                    title="Total Events"
                    value={publishedEvents.length}
                    icon={Calendar}
                    subtext="Published events"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Average Capacity"
                    value={avgCapacity}
                    icon={Users}
                    subtext="Per event"
                    scheme="green"
                  />
                  <MetricCard
                    title="Virtual Events"
                    value={virtualEvents.length}
                    icon={Network}
                    subtext="Online events"
                    scheme="orange"
                  />
                  <MetricCard
                    title="Physical Events"
                    value={physicalEvents.length}
                    icon={Building}
                    subtext="In-person events"
                    scheme="blue"
                  />
                </div>

                {/* Event Type Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ChartCard 
                    title="Event Type Distribution" 
                    subtitle="Events by category"
                    icon={BookOpen}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(events.reduce((acc, event) => {
                              acc[event.type] = (acc[event.type] || 0) + 1;
                              return acc;
                            }, {})).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.entries(events.reduce((acc, event) => {
                              acc[event.type] = (acc[event.type] || 0) + 1;
                              return acc;
                            }, {})).map((_, index) => (
                              <Cell 
                                key={`cell-${index}`}
                                fill={CHART_COLORS.primary[
                                  Object.keys(CHART_COLORS.primary)[index % 
                                    Object.keys(CHART_COLORS.primary).length]
                                ][0]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                      </div>
                  </ChartCard>

                  {/* Venue Distribution */}
                  <ChartCard 
                    title="Venue Distribution" 
                    subtitle="Physical vs Virtual events"
                    icon={MapPin}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { 
                                name: 'Physical', 
                                value: events.filter(e => !e.venue.isVirtual).length 
                              },
                              { 
                                name: 'Virtual', 
                                value: events.filter(e => e.venue.isVirtual).length 
                              }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill={CHART_COLORS.primary.orange[0]} />
                            <Cell fill={CHART_COLORS.primary.blue[0]} />
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Recent Events */}
                <ChartCard 
                  title="Recent Events" 
                  subtitle="Latest event activities"
                  icon={Calendar}
                  scheme="purple"
                >
                  <div className="space-y-4">
                    {events.slice(0, 5).map((event, index) => (
                      <div key={event._id} 
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl
                          hover:bg-gray-100 transition-all group"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${
                            CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                          }`}>
                            <Calendar className={`h-5 w-5 ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{event.title}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(event.schedule.startDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            Capacity: {event.capacity.max}
                          </p>
                          <p className="text-sm text-gray-500">{event.venue.isVirtual ? 'Virtual' : event.venue.city}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
              <div className="space-y-6">
                {/* Attendance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Total Capacity"
                    value={totalCapacity}
                    icon={Users}
                    subtext="All events combined"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Avg. Capacity"
                    value={avgCapacity}
                    icon={Target}
                    subtext="Per event"
                    scheme="green"
                  />
                  <MetricCard
                    title="Max Capacity"
                    value={Math.max(...events.map(event => event.capacity.max))}
                    icon={Activity}
                    subtext="Highest capacity"
                    scheme="orange"
                  />
                </div>

                {/* Capacity Analysis */}
                <ChartCard 
                  title="Capacity Distribution" 
                  subtitle="Event size analysis"
                  icon={Users}
                  scheme="purple"
                >
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { range: '< 100', count: events.filter(e => e.capacity.max < 100).length },
                          { range: '100-500', count: events.filter(e => e.capacity.max >= 100 && e.capacity.max < 500).length },
                          { range: '500-1000', count: events.filter(e => e.capacity.max >= 500 && e.capacity.max < 1000).length },
                          { range: '1000+', count: events.filter(e => e.capacity.max >= 1000).length }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count">
                          {[0, 1, 2, 3].map((index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={CHART_COLORS.primary.purple[index]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                {/* Timeline Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Upcoming Events"
                    value={events.filter(e => new Date(e.schedule.startDate) > new Date()).length}
                    icon={Calendar}
                    subtext="Yet to start"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Ongoing Events"
                    value={events.filter(e => {
                      const now = new Date();
                      return new Date(e.schedule.startDate) <= now && 
                             new Date(e.schedule.endDate) >= now;
                    }).length}
                    icon={Activity}
                    subtext="Currently active"
                    scheme="green"
                  />
                  <MetricCard
                    title="Past Events"
                    value={events.filter(e => new Date(e.schedule.endDate) < new Date()).length}
                    icon={Check}
                    subtext="Completed events"
                    scheme="orange"
                  />
                </div>

                {/* Event Distribution Over Time */}
                <ChartCard 
                  title="Event Timeline Distribution" 
                  subtitle="Events by status over time"
                  icon={Clock}
                  scheme="purple"
                >
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={events
                          .sort((a, b) => new Date(a.schedule.startDate) - new Date(b.schedule.startDate))
                          .map(event => {
                            const now = new Date();
                            const startDate = new Date(event.schedule.startDate);
                            const endDate = new Date(event.schedule.endDate);
                            return {
                              date: startDate.toLocaleDateString(),
                              upcoming: startDate > now ? 1 : 0,
                              ongoing: (startDate <= now && endDate >= now) ? 1 : 0,
                              completed: endDate < now ? 1 : 0
                            };
                          })}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="upcoming" 
                          stackId="1"
                          stroke={CHART_COLORS.primary.orange[0]} 
                          fill={CHART_COLORS.primary.orange[0]} 
                          name="Upcoming"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="ongoing" 
                          stackId="1"
                          stroke={CHART_COLORS.primary.blue[0]} 
                          fill={CHART_COLORS.primary.blue[0]} 
                          name="Ongoing"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="completed" 
                          stackId="1"
                          stroke={CHART_COLORS.primary.green[0]} 
                          fill={CHART_COLORS.primary.green[0]} 
                          name="Completed"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Upcoming Events"
                    value={events.filter(e => new Date(e.schedule.startDate) > new Date()).length}
                    icon={Calendar}
                    subtext="Yet to start"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Ongoing Events"
                    value={events.filter(e => {
                      const now = new Date();
                      return new Date(e.schedule.startDate) <= now && 
                             new Date(e.schedule.endDate) >= now;
                    }).length}
                    icon={Activity}
                    subtext="Currently active"
                    scheme="green"
                  />
                  <MetricCard
                    title="Past Events"
                    value={events.filter(e => new Date(e.schedule.endDate) < new Date()).length}
                    icon={Check}
                    subtext="Completed events"
                    scheme="orange"
                  />
                </div>

                {/* Event Timeline */}
                <ChartCard 
                  title="Event Schedule" 
                  subtitle="Timeline of events"
                  icon={Clock}
                  scheme="purple"
                >
                  <div className="space-y-4">
                    {events
                      .sort((a, b) => new Date(a.schedule.startDate) - new Date(b.schedule.startDate))
                      .map((event, index) => {
                        const startDate = new Date(event.schedule.startDate);
                        const endDate = new Date(event.schedule.endDate);
                        const now = new Date();
                        let status = 'Upcoming';
                        if (startDate <= now && endDate >= now) status = 'Ongoing';
                        if (endDate < now) status = 'Completed';

                        return (
                          <div key={event._id} 
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-lg ${
                                status === 'Completed' ? 'bg-green-100 text-green-600' :
                                status === 'Ongoing' ? 'bg-blue-100 text-blue-600' :
                                'bg-orange-100 text-orange-600'
                              }`}>
                                <Clock className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{event.title}</p>
                                <p className="text-sm text-gray-500">
                                  {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              status === 'Completed' ? 'bg-green-100 text-green-600' :
                              status === 'Ongoing' ? 'bg-blue-100 text-blue-600' :
                              'bg-orange-100 text-orange-600'
                            }`}>
                              {status}
                            </span>
                          </div>
                        );
                    })}
                  </div>
                </ChartCard>
              </div>
            )}

            {/* Venues Tab */}
            {activeTab === 'venues' && (
              <div className="space-y-6">
                {/* Venue Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Physical Venues"
                    value={physicalEvents.length}
                    icon={Building}
                    subtext="On-site events"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Virtual Events"
                    value={virtualEvents.length}
                    icon={Network}
                    subtext="Online events"
                    scheme="green"
                  />
                  <MetricCard
                    title="Cities"
                    value={new Set(events.filter(e => !e.venue.isVirtual)
                      .map(e => e.venue.city)).size}
                    icon={MapPin}
                    subtext="Unique locations"
                    scheme="orange"
                  />
                </div>

                {/* Venue Capacity Distribution */}
                <ChartCard 
                  title="Venue Capacity Analysis" 
                  subtitle="Capacity distribution by venue type"
                  icon={Users}
                  scheme="purple"
                >
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          {
                            type: 'Physical',
                            averageCapacity: Math.round(
                              physicalEvents.reduce((sum, e) => sum + e.capacity.max, 0) / 
                              (physicalEvents.length || 1)
                            ),
                            totalCapacity: physicalEvents.reduce((sum, e) => sum + e.capacity.max, 0),
                            count: physicalEvents.length
                          },
                          {
                            type: 'Virtual',
                            averageCapacity: Math.round(
                              virtualEvents.reduce((sum, e) => sum + e.capacity.max, 0) / 
                              (virtualEvents.length || 1)
                            ),
                            totalCapacity: virtualEvents.reduce((sum, e) => sum + e.capacity.max, 0),
                            count: virtualEvents.length
                          }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          name="Average Capacity" 
                          dataKey="averageCapacity" 
                          fill={CHART_COLORS.primary.purple[0]} 
                        />
                        <Bar 
                          name="Event Count" 
                          dataKey="count" 
                          fill={CHART_COLORS.primary.green[0]} 
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Physical Venues"
                    value={physicalEvents.length}
                    icon={Building}
                    subtext="On-site events"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Virtual Events"
                    value={virtualEvents.length}
                    icon={Network}
                    subtext="Online events"
                    scheme="green"
                  />
                  <MetricCard
                    title="Cities"
                    value={new Set(events.filter(e => !e.venue.isVirtual)
                      .map(e => e.venue.city)).size}
                    icon={MapPin}
                    subtext="Unique locations"
                    scheme="orange"
                  />
                </div>

                {/* Physical Venues List */}
                <ChartCard 
                  title="Physical Venues" 
                  subtitle="On-site event locations"
                  icon={Building}
                  scheme="purple"
                >
                  <div className="space-y-4">
                    {events
                      .filter(e => !e.venue.isVirtual)
                      .map((event, index) => (
                        <div key={event._id} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                            }`}>
                              <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{event.venue.name || 'Unnamed Venue'}</p>
                              <p className="text-sm text-gray-500">
                                {event.venue.city}, {event.venue.state}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              Capacity: {event.capacity.max}
                            </p>
                            <p className="text-sm text-gray-500">{event.title}</p>
                          </div>
                        </div>
                    ))}
                  </div>
                </ChartCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventAnalytics;